import { UnitOfMeasure } from '@usf/mpp-types';
import {
  UnitOfMeasureLabels,
  CONVERSION_CONSTANTS,
  UnitType,
} from '../enum/unit-conversion-enums';

export class UnitConversionHelper {
  public static isConversionAllowed(
    fromType: UnitType,
    toType: UnitType,
    isUSFProduct: boolean,
  ): boolean {
    if (fromType === toType) return false;

    if (isUSFProduct) {
      // Special rules for USF products
      if (fromType === UnitType.LIQUID && toType === UnitType.SOLID)
        return false;
      if (fromType === UnitType.PIECE && toType === UnitType.SOLID)
        return false;
    }

    return true;
  }

  public static convertToLB(value: number, fromUnit: UnitOfMeasureLabels) {
    let calcualtedLbs = 0;
    switch (fromUnit) {
      case UnitOfMeasureLabels.POUND:
        return (calcualtedLbs = value);
      case UnitOfMeasureLabels.OUNCE:
        return (calcualtedLbs = value / CONVERSION_CONSTANTS.POUND_TO_OUNCE);
      case UnitOfMeasureLabels.GRAMS:
        return (calcualtedLbs = value / CONVERSION_CONSTANTS.POUND_TO_GRAMS);
      default:
        return calcualtedLbs;
    }
  }
  public static convertAndUpdateSolidMeasures(
    value: number,
    fromUnit: UnitOfMeasureLabels,
    measures: Partial<UnitOfMeasure> = {},
  ): Partial<UnitOfMeasure> {
    if (value === 0) {
      return {
        ...measures,
        pound: 0,
        ounce: 0,
        grams: 0,
      };
    }

    // First convert to grams as base unit
    let grams: number;
    switch (fromUnit) {
      case UnitOfMeasureLabels.POUND:
        grams = value * CONVERSION_CONSTANTS.POUND_TO_GRAMS;
        break;
      case UnitOfMeasureLabels.OUNCE:
        grams = value * CONVERSION_CONSTANTS.OUNCE_TO_GRAMS;
        break;
      case UnitOfMeasureLabels.GRAMS:
        grams = value;
        break;
      default:
        return measures;
    }

    // Update all solid measures
    const updatedMeasures: Partial<UnitOfMeasure> = {
      ...measures,
      grams: grams,
      pound: grams / CONVERSION_CONSTANTS.POUND_TO_GRAMS,
      ounce: grams / CONVERSION_CONSTANTS.OUNCE_TO_GRAMS,
    };

    // If we have cups defined, update cupWeight
    if (measures.cup && measures.cup > 0) {
      updatedMeasures.cupWeight = grams / measures.cup;
    }

    return updatedMeasures;
  }

  public static convertToCup(value: number, fromUnit: UnitOfMeasureLabels) {
    // First convert to cups as base unit
    let cups: number;
    switch (fromUnit) {
      case UnitOfMeasureLabels.CUP:
        cups = value;
        break;
      case UnitOfMeasureLabels.GALLON:
        cups = value * CONVERSION_CONSTANTS.GALLON_TO_CUP;
        break;
      case UnitOfMeasureLabels.QUART:
        cups = value * CONVERSION_CONSTANTS.QUART_TO_CUP;
        break;
      case UnitOfMeasureLabels.PINT:
        cups = value * CONVERSION_CONSTANTS.PINT_TO_CUP;
        break;
      case UnitOfMeasureLabels.LIQUID_OUNCE:
        cups = value / CONVERSION_CONSTANTS.CUP_TO_LIQUID_OUNCE;
        break;
      case UnitOfMeasureLabels.TABLESPOON:
        cups = value / CONVERSION_CONSTANTS.CUP_TO_TABLESPOON;
        break;
      case UnitOfMeasureLabels.TEASPOON:
        cups = value / CONVERSION_CONSTANTS.CUP_TO_TEASPOON;
        break;
      case UnitOfMeasureLabels.LITER:
        cups = value * CONVERSION_CONSTANTS.LITER_TO_CUP;
        break;
      default:
        if (fromUnit.startsWith('SCOOP_')) {
          const scoopSize = fromUnit.split('_')[1];
          cups =
            value * CONVERSION_CONSTANTS.SCOOP_TO_CUP[`SCOOP_${scoopSize}`];
          break;
        }
        if (fromUnit.startsWith('LADLE_')) {
          cups = value * CONVERSION_CONSTANTS.LADLE_TO_CUP[fromUnit];
          break;
        }
        cups = 0;
    }

    return cups;
  }

  public static convertAndUpdateLiquidMeasures(
    value: number,
    fromUnit: UnitOfMeasureLabels,
    measures: Partial<UnitOfMeasure> = {},
  ): Partial<UnitOfMeasure> {
    if (value === 0) {
      return {
        ...measures,
        cup: 0,
        gallon: 0,
        quart: 0,
        pint: 0,
        liquidOunce: 0,
        tablespoon: 0,
        teaspoon: 0,
        liters: 0,
        scoop6: 0,
        scoop8: 0,
        scoop10: 0,
        scoop12: 0,
        scoop16: 0,
        scoop20: 0,
        scoop24: 0,
        scoop30: 0,
        scoop40: 0,
        scoop50: 0,
        scoop60: 0,
        scoop70: 0,
        scoop100: 0,
        ladle1: 0,
        ladle2: 0,
        ladle3: 0,
        ladle4: 0,
        ladle5: 0,
        ladle6: 0,
        ladle8: 0,
        ladle12: 0,
      };
    }

    // First convert to cups as base unit
    let cups: number;
    switch (fromUnit) {
      case UnitOfMeasureLabels.CUP:
        cups = value;
        break;
      case UnitOfMeasureLabels.GALLON:
        cups = value * CONVERSION_CONSTANTS.GALLON_TO_CUP;
        break;
      case UnitOfMeasureLabels.QUART:
        cups = value * CONVERSION_CONSTANTS.QUART_TO_CUP;
        break;
      case UnitOfMeasureLabels.PINT:
        cups = value * CONVERSION_CONSTANTS.PINT_TO_CUP;
        break;
      case UnitOfMeasureLabels.LIQUID_OUNCE:
        cups = value / CONVERSION_CONSTANTS.CUP_TO_LIQUID_OUNCE;
        break;
      case UnitOfMeasureLabels.TABLESPOON:
        cups = value / CONVERSION_CONSTANTS.CUP_TO_TABLESPOON;
        break;
      case UnitOfMeasureLabels.TEASPOON:
        cups = value / CONVERSION_CONSTANTS.CUP_TO_TEASPOON;
        break;
      case UnitOfMeasureLabels.LITER:
        cups = value * CONVERSION_CONSTANTS.LITER_TO_CUP;
        break;
      default:
        if (fromUnit.startsWith('SCOOP_')) {
          const scoopSize = fromUnit.split('_')[1];
          cups =
            value * CONVERSION_CONSTANTS.SCOOP_TO_CUP[`SCOOP_${scoopSize}`];
          break;
        }
        if (fromUnit.startsWith('LADLE_')) {
          cups = value * CONVERSION_CONSTANTS.LADLE_TO_CUP[fromUnit];
          break;
        }
        return measures;
    }

    const updatedMeasures: Partial<UnitOfMeasure> = {
      ...measures,
      cup: cups,
      gallon: cups / CONVERSION_CONSTANTS.GALLON_TO_CUP,
      quart: cups / CONVERSION_CONSTANTS.QUART_TO_CUP,
      pint: cups / CONVERSION_CONSTANTS.PINT_TO_CUP,
      liquidOunce: cups * CONVERSION_CONSTANTS.CUP_TO_LIQUID_OUNCE,
      tablespoon: cups * CONVERSION_CONSTANTS.CUP_TO_TABLESPOON,
      teaspoon: cups * CONVERSION_CONSTANTS.CUP_TO_TEASPOON,
      liters: cups / CONVERSION_CONSTANTS.LITER_TO_CUP,
    };

    // Update scoop measures
    Object.entries(CONVERSION_CONSTANTS.SCOOP_TO_CUP).forEach(
      ([scoop, conversion]) => {
        const scoopKey = `scoop${scoop.split('_')[1]}` as keyof UnitOfMeasure;
        updatedMeasures[scoopKey] = cups / conversion;
      },
    );

    // Update ladle measures
    Object.entries(CONVERSION_CONSTANTS.LADLE_TO_CUP).forEach(
      ([ladle, conversion]) => {
        const ladleKey =
          `ladle${ladle.split('_')[1].split('OZ')[0]}` as keyof UnitOfMeasure;
        updatedMeasures[ladleKey] = cups / conversion;
      },
    );

    // If we have grams defined, update cupWeight
    if (measures.grams && measures.grams > 0 && cups > 0) {
      updatedMeasures.cupWeight = measures.grams / cups;
    }

    return updatedMeasures;
  }

  public static setPieces(
    valueToSave: number,
    measures: Partial<UnitOfMeasure> = {},
  ) {
    return {
      ...measures,
      portion: valueToSave,
      slice: valueToSave,
    };
  }

  /**
   * Determines the unit type for a given unit of measure
   */
  public static getUnitType(unit: UnitOfMeasureLabels): UnitType {
    if (
      [
        UnitOfMeasureLabels.POUND,
        UnitOfMeasureLabels.OUNCE,
        UnitOfMeasureLabels.GRAMS,
      ].includes(unit)
    ) {
      return UnitType.SOLID;
    }
    if (
      [
        UnitOfMeasureLabels.PIECE,
        UnitOfMeasureLabels.SLICE,
        UnitOfMeasureLabels.EACH,
      ].includes(unit)
    ) {
      return UnitType.PIECE;
    }
    return UnitType.LIQUID;
  }

  public static roundToTwoDecimals(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  public static getDisplayFriendlyUOM(unit: UnitOfMeasureLabels): string {
    switch (unit) {
      // Solid measures
      case UnitOfMeasureLabels.POUND:
        return 'i18n.menuProfitPro.uomOptions.pound';
      case UnitOfMeasureLabels.OUNCE:
        return 'i18n.menuProfitPro.uomOptions.ounce';
      case UnitOfMeasureLabels.GRAMS:
        return 'i18n.menuProfitPro.uomOptions.gram';

      // Liquid measures
      case UnitOfMeasureLabels.CUP:
        return 'i18n.menuProfitPro.uomOptions.cup';
      case UnitOfMeasureLabels.GALLON:
        return 'i18n.menuProfitPro.uomOptions.gallon';
      case UnitOfMeasureLabels.QUART:
        return 'i18n.menuProfitPro.uomOptions.quart';
      case UnitOfMeasureLabels.PINT:
        return 'i18n.menuProfitPro.uomOptions.pint';
      case UnitOfMeasureLabels.LIQUID_OUNCE:
        return 'i18n.menuProfitPro.uomOptions.liquid';
      case UnitOfMeasureLabels.TABLESPOON:
        return 'i18n.menuProfitPro.uomOptions.tbsp';
      case UnitOfMeasureLabels.TEASPOON:
        return 'i18n.menuProfitPro.uomOptions.tsp';
      case UnitOfMeasureLabels.LITER:
        return 'Liters';
      case UnitOfMeasureLabels.MILLILITER:
        return 'Milliliters';

      // Piece measures
      case UnitOfMeasureLabels.PIECE:
        return 'Piece';
      case UnitOfMeasureLabels.EACH:
        return 'i18n.menuProfitPro.uomOptions.each';
      case UnitOfMeasureLabels.SLICE:
        return 'Slices';

      // Special measures
      case UnitOfMeasureLabels.CUP_WEIGHT:
        return 'Cup Weight';

      // Scoop measures
      case UnitOfMeasureLabels.SCOOP_6:
        return '6 Scoop';
      case UnitOfMeasureLabels.SCOOP_8:
        return '8 Scoop';
      case UnitOfMeasureLabels.SCOOP_10:
        return '10 Scoop';
      case UnitOfMeasureLabels.SCOOP_12:
        return '12 Scoop';
      case UnitOfMeasureLabels.SCOOP_16:
        return '16 Scoop';
      case UnitOfMeasureLabels.SCOOP_20:
        return '20 Scoop';
      case UnitOfMeasureLabels.SCOOP_24:
        return '24 Scoop';
      case UnitOfMeasureLabels.SCOOP_30:
        return '30 Scoop';
      case UnitOfMeasureLabels.SCOOP_40:
        return '40 Scoop';
      case UnitOfMeasureLabels.SCOOP_50:
        return '50 Scoop';
      case UnitOfMeasureLabels.SCOOP_60:
        return '60 Scoop';
      case UnitOfMeasureLabels.SCOOP_70:
        return '70 Scoop';
      case UnitOfMeasureLabels.SCOOP_100:
        return '100 Scoop';

      // Ladle measures
      case UnitOfMeasureLabels.LADLE_1_OZ:
        return '1oz Ladle';
      case UnitOfMeasureLabels.LADLE_2_OZ:
        return '2oz Ladle';
      case UnitOfMeasureLabels.LADLE_3_OZ:
        return '3oz Ladle';
      case UnitOfMeasureLabels.LADLE_4_OZ:
        return '4oz Ladle';
      case UnitOfMeasureLabels.LADLE_5_OZ:
        return '5oz Ladle';
      case UnitOfMeasureLabels.LADLE_6_OZ:
        return '6oz Ladle';
      case UnitOfMeasureLabels.LADLE_8_OZ:
        return '8oz Ladle';
      case UnitOfMeasureLabels.LADLE_12_OZ:
        return '12oz Ladle';

      default:
        return '';
    }
  }
}
